import { useCallback } from 'react';
import { useMutation, useQuery } from '@zg-rentals/react-query5';
import { shallowEqual, useDispatch } from 'react-redux';

import useRequestConfig from '../../hooks/useRequestConfig';

import { useSelector } from '../../selectors/useSelector';

import makeRequest from '../../utils/makeRequestUtils';

import StringConstants from '../../constants/StringConstants';
import { RENTAL_MANAGER_API_V1_PREFIX, RENTAL_MANAGER_API_V2_PREFIX } from '../../constants/RouteConstants';

import { HelpArticles } from '../../model/HelpArticles';
import { HelpTopicsList } from '../../model/HelpTopics';

import type { HelpArticlesProps } from '../../model/HelpArticles';
import type { HelpTopicsListProps } from '../../model/HelpTopics';
import type SupportRequestBody from '../../model/SupportRequest';

/**
 * Gets support articles by category and topic
 *
 * @param {string} [category]
 * @param {string} [topic]
 */
export const useGetHelpArticles = ({ category = '', topic = '' }, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const { loggedIn } = useSelector((state) => ({ loggedIn: state.user.loggedIn }), shallowEqual);

  const queryFn = useCallback(
    () =>
      makeRequest({
        dispatch,
        endpoint: loggedIn
          ? `${RENTAL_MANAGER_API_V1_PREFIX}/users/support/getHelpArticles`
          : `${RENTAL_MANAGER_API_V1_PREFIX}/support/getHelpArticles`,
        requestConfig,
        config: {
          params: { category, topic },
        },
      }),
    [category, topic, queryOptions, dispatch, requestConfig, loggedIn],
  );

  return useQuery<HelpArticlesProps, Error>({
    queryKey: ['getHelpArticles', category, topic],
    queryFn: async () => {
      const data = await queryFn();
      return HelpArticles(data);
    },

    ...queryOptions,
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Gets all help topics by category
 */
export const useGetHelpTopics = ({ isLoggedIn = false }, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const endpoint = isLoggedIn
    ? `${RENTAL_MANAGER_API_V1_PREFIX}/users/support/getHelpTopics`
    : `${RENTAL_MANAGER_API_V2_PREFIX}/support/getHelpTopics`;

  const queryFn = useCallback(
    () =>
      makeRequest({
        dispatch,
        endpoint,
        requestConfig,
      }),
    [queryOptions, dispatch, requestConfig],
  );

  return useQuery<HelpTopicsListProps, Error>({
    queryKey: ['getHelpTopics'],
    queryFn: async () => {
      const data = await queryFn();
      return HelpTopicsList(data);
    },
    ...queryOptions,
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Submits request for logged in user
 *
 * @param {string} category e.g. "myListing"
 * @param {string} topic
 * @param {string} [email]
 * @param {string} [firstName]
 * @param {string} [lastName]
 * @param {string} listingAddress - required for myListing category
 * @param {string} [unit]
 * @param {string} description
 * @param {string} [listingAlias]
 */
export const useSubmitUserRequest = (queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(StringConstants.POST);

  const mutationFn = (data: SupportRequestBody) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V2_PREFIX}/users/support/submitRequest`,
      requestConfig,
      config: { data },
    });

  return useMutation({ mutationFn, ...queryOptions });
};

/**
 * Submits request for unknown user
 *
 * @param {string} category e.g. "myListing"
 * @param {string} topic
 * @param {string} [email]
 * @param {string} [firstName]
 * @param {string} [lastName]
 * @param {string} listingAddress - required for myListing category
 * @param {string} [unit]
 * @param {string} description
 * @param {string} [listingAlias]
 */
export const useSubmitRequest = (queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(StringConstants.POST);

  const mutationFn = (data: SupportRequestBody) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V2_PREFIX}/support/submitRequest`,
      requestConfig,
      config: { data },
    });

  return useMutation({ mutationFn, ...queryOptions });
};
